// resume_autofill_controller.js
// Receives parsed resume fields and prefills the candidate profile edit form.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { fields: Object, autoApply: Boolean };

  connect() {
    if (this.autoApplyValue && this.fieldsValue && Object.keys(this.fieldsValue).length > 0) {
      this.apply();
    }
  }

  apply() {
    const fields = this.fieldsValue;
    const form = document.querySelector("form[data-profile-form]") || document.querySelector("form");
    if (!form) return;

    Object.entries(fields).forEach(([key, value]) => {
      this.fillField(form, key, value);
    });

    if (Array.isArray(fields.preferred_industries) && fields.preferred_industries[0]) {
      const industrySelect = form.querySelector('[name="candidate_profile[industry]"]');
      if (industrySelect && !industrySelect.value) {
        this.selectOption(industrySelect, fields.preferred_industries[0]);
        industrySelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // Show a brief success indicator
    this.element.querySelector("[data-action='resume-autofill#apply']")?.remove();
    const msg = document.createElement("p");
    msg.className = "mt-2 text-xs font-medium text-green-800";
    msg.textContent = "✓ Fields prefilled — review and save your profile.";
    this.element.appendChild(msg);
  }

  dismiss() {
    this.element.innerHTML = "";
  }

  // private

  fillField(form, key, value) {
    // Scalar text fields
    const scalarSelectors = [
      `[name="candidate_profile[${key}]"]`,
      `[name*="[${key}]"]`,
    ];

    for (const sel of scalarSelectors) {
      const el = form.querySelector(sel);
      if (!el) continue;

      if (el.tagName === "SELECT" && !el.multiple) {
        this.selectOption(el, value);
        return;
      }
      if (el.tagName === "TEXTAREA" || el.type === "text" || el.type === "number" || el.type === "url") {
        if (el.value === "" || el.value === "0") {
          el.value = value;
          el.dispatchEvent(new Event("input", { bubbles: true }));
          el.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }
      // Trix rich text editor — find the underlying <trix-editor>
      if (el.type === "hidden" && el.closest("trix-editor, [data-rich-text-area]")) {
        const trix = form.querySelector(`trix-editor[input="${el.id}"]`);
        if (trix && trix.editor && trix.editor.getDocument().toString().trim() === "\n") {
          trix.editor.loadHTML(`<p>${this.escapeHtml(value)}</p>`);
        }
        return;
      }
    }

    // Array fields (multi-selects)
    if (Array.isArray(value)) {
      const multiSel = form.querySelector(`select[name="candidate_profile[${key}][]"], select[name*="[${key}][]"]`);
      if (multiSel) {
        this.selectMultipleOptions(multiSel, value);
        return;
      }
    }

    // Trix-backed rich text (bio, experience, education) — the hidden input name ends in [${key}]
    const trixInput = form.querySelector(`input[name="candidate_profile[${key}]"][type="hidden"]`);
    if (trixInput) {
      const trix = form.querySelector(`trix-editor[input="${trixInput.id}"]`);
      if (trix && trix.editor && trix.editor.getDocument().toString().trim() === "\n") {
        trix.editor.loadHTML(`<p>${this.escapeHtml(String(value))}</p>`);
      }
    }
  }

  selectOption(select, value) {
    const lower = String(value).toLowerCase().replace(/[\s_-]/g, "");
    let matched = false;
    for (const opt of select.options) {
      const optLower = opt.value.toLowerCase().replace(/[\s_-]/g, "");
      const optTextLower = opt.text.toLowerCase().replace(/[\s_-]/g, "");
      if (optLower === lower || optTextLower === lower || optLower.includes(lower) || lower.includes(optLower)) {
        opt.selected = true;
        matched = true;
        break;
      }
    }
    if (matched) select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  selectMultipleOptions(select, values) {
    const lowers = values.map((v) => v.toLowerCase().replace(/[\s_-]/g, ""));
    let changed = false;
    for (const opt of select.options) {
      const optLower = opt.value.toLowerCase().replace(/[\s_-]/g, "");
      const optTextLower = opt.text.toLowerCase().replace(/[\s_-]/g, "");
      if (lowers.some((l) => optLower.includes(l) || l.includes(optLower) || optTextLower.includes(l))) {
        opt.selected = true;
        changed = true;
      }
    }
    if (changed) select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  escapeHtml(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
